import { forwardRef, ReactNode } from 'react';

import { InputBase, InputBaseProps } from '@any-ui-react/core';
import classNames from 'classnames';
import { Popover } from 'radix-ui';

import { ChevronDownIcon, ClearIcon } from '@anyx/shared-icons';

import { comboboxStyles } from '../combobox.styles';
import { useComboboxContext } from '../ComboboxContext';

export interface ComboboxButtonTriggerProps
  extends Omit<InputBaseProps, 'rightSection' | 'component'> {
  children?: ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

export const ComboboxButtonTrigger = forwardRef<HTMLButtonElement, ComboboxButtonTriggerProps>(
  ({ clearable, onClear, children, className, onClick, ...rest }, ref) => {
    const { classNames: contextClassNames, disabled, invalid } = useComboboxContext();
    const styles = comboboxStyles();

    const hasValue = !!children;

    const rightSection =
      clearable && hasValue ? (
        <button
          type="button"
          className="flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          onMouseDown={(e) => e.preventDefault()}
          tabIndex={-1}
          aria-label="Clear"
        >
          <ClearIcon className="fill-gray-5 h-3.5 w-3.5 cursor-pointer" />
        </button>
      ) : (
        <ChevronDownIcon className="text-gray-5 h-4 w-4" />
      );

    return (
      <Popover.Trigger asChild>
        <InputBase
          {...rest}
          ref={ref}
          component="button"
          type="button"
          disabled={disabled}
          error={invalid}
          className={classNames(
            styles.buttonTrigger(),
            contextClassNames?.buttonTrigger,
            className
          )}
          rightSection={rightSection}
          rightSectionPointerEvents={clearable && hasValue ? 'all' : 'none'}
          pointer
          onClick={(e) => {
            onClick?.(e as React.MouseEvent<HTMLButtonElement>);
          }}
        >
          {children}
        </InputBase>
      </Popover.Trigger>
    );
  }
);
